import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
  AutoStories,
} from '@mui/icons-material';
import { UploadSession, DeviceInfo } from '../types';
import { BreakfastCard } from './breakfast/BreakfastCard';
import { BreakfastColors } from '../theme/BreakfastTheme';

interface SessionStatusProps {
  session: UploadSession;
  deviceInfo: DeviceInfo;
}

const SessionStatus: React.FC<SessionStatusProps> = ({ session, deviceInfo }) => {
  const getStatusInfo = () => {
    switch (session.status) {
      case 'validating':
        return {
          icon: <CircularProgress size={24} sx={{ color: BreakfastColors.primary }} />,
          title: 'Validating session...',
          message: 'Checking your upload link...',
          backgroundColor: BreakfastColors.primaryFill,
          borderColor: BreakfastColors.primary + '33', // 20% opacity
          titleColor: BreakfastColors.primary,
        };
      
      case 'ready':
        return {
          icon: <CheckCircle sx={{ color: BreakfastColors.success, fontSize: 24 }} />,
          title: 'Ready to upload',
          message: session.prompt || 'Your upload session is ready',
          backgroundColor: BreakfastColors.success + '1A', // 10% opacity
          borderColor: BreakfastColors.success + '4D', // 30% opacity
          titleColor: BreakfastColors.success,
        };
      
      case 'uploading':
        return {
          icon: <CloudUpload sx={{ color: BreakfastColors.primary, fontSize: 24 }} />,
          title: 'Uploading Photos',
          message: 'Your photos are being uploaded...',
          backgroundColor: BreakfastColors.primaryFill,
          borderColor: BreakfastColors.primary + '33', // 20% opacity
          titleColor: BreakfastColors.primary,
        };
      
      case 'uploaded':
        return {
          icon: <CheckCircle sx={{ color: BreakfastColors.success, fontSize: 24 }} />,
          title: 'Upload Complete',
          message: 'All photos have been successfully uploaded',
          backgroundColor: BreakfastColors.success + '1A', // 10% opacity
          borderColor: BreakfastColors.success + '4D', // 30% opacity
          titleColor: BreakfastColors.success,
        };
      
      case 'complete':
        return {
          icon: <AutoStories sx={{ color: BreakfastColors.success, fontSize: 24 }} />,
          title: 'Session Complete',
          message: 'Thank you! Your book request has been submitted.',
          backgroundColor: BreakfastColors.success + '1A', // 10% opacity
          borderColor: BreakfastColors.success + '4D', // 30% opacity
          titleColor: BreakfastColors.success,
        };
      
      case 'error':
      default:
        return {
          icon: <ErrorIcon sx={{ color: BreakfastColors.notify, fontSize: 24 }} />,
          title: 'Invalid session',
          message: session.prompt || 'Invalid Upload Link. Please contact support.',
          backgroundColor: BreakfastColors.notifyFill, // 0x1AFF9500
          borderColor: BreakfastColors.notify + '4D', // 30% opacity
          titleColor: BreakfastColors.notify,
        };
    }
  };

  const statusInfo = getStatusInfo();

  if (session.status === 'complete') {
    return null; // Don't show status card when complete (handled in main component)
  }

  return (
    <BreakfastCard
      backgroundColor={statusInfo.backgroundColor}
      borderColor={statusInfo.borderColor}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        {/* Status Icon */}
        {statusInfo.icon}
        
        {/* Status Content */}
        <Box flex={1}>
          <Typography 
            variant="h3"
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: '"CircularXX", sans-serif',
              color: statusInfo.titleColor,
              letterSpacing: '-0.2px',
              marginBottom: statusInfo.message ? 0.75 : 0,
              lineHeight: 1.3,
            }}
          >
            {statusInfo.title}
          </Typography>
          
          {statusInfo.message && (
            <Typography 
              variant="body1"
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: '"CircularXX", sans-serif',
                color: BreakfastColors.subtitle,
                letterSpacing: '-0.1px',
                lineHeight: 1.4,
              }}
            >
              {statusInfo.message}
            </Typography>
          )}
        </Box>
      </Box>
    </BreakfastCard>
  );
};

export default SessionStatus;
